"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Account types",
    rule: "Bank (user-added), Petty Cash and Payment Clearing (system-provided) — only Bank accounts are created via Add Bank",
  },
  {
    topic: "Primary bank",
    rule: "Exactly one account should be marked Primary via Make this primary. Quotes, Sales Orders, Delivery Challans, and Invoices cannot be created until a primary bank exists — its details print on those PDFs",
  },
  {
    topic: "Currency",
    rule: "Required at creation, permanently locked afterward",
  },
  {
    topic: "Deposit / Withdraw / Transfer",
    rule: "All three post to the account's Bank History and to Bank Transfer Logs as Deposit / Withdrawal / Transfer respectively",
  },
  {
    topic: "Cross-currency transfers",
    rule: "Converted automatically; both currencies are recorded on the entry",
  },
  {
    topic: "Approval",
    rule: "Transfer Logs carry a Transaction Status (Approved / Cancelled observed) and an Approved-By field; an Approved transfer can be Cancelled afterward. Whether new transfers ever sit in a Pending state depends on role permissions.",
  },
  {
    topic: "Deleting / deactivating",
    rule: "Not available — there is no delete action and no active/inactive toggle anywhere on this module",
  },
  { topic: "Editing", rule: "Every field is editable except Currency" },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Three kinds of accounts, one balance pool" },
      { heading: "2. Adding an account links it to your ledger" },
      { heading: "3. Deposit, Withdraw, and Transfer are one mechanism" },
      { heading: "4. Every account is a live mirror, not a manual record" },
      { heading: "5. What's locked and what isn't" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function BanksPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Banks
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording company bank, cash, or petty-cash accounts and
            moving money between them
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            This is where every account your money can sit in or move through
            lives, not just your bank accounts but{" "}
            <span className="geist-medium text-[#111827]">Petty Cash</span> and
            a couple of accounts the system sets up on its own. Each one keeps
            a running balance, and any{" "}
            <span className="geist-medium text-[#111827]">deposit</span>,{" "}
            <span className="geist-medium text-[#111827]">withdrawal</span>, or{" "}
            <span className="geist-medium text-[#111827]">transfer</span> you
            make posts straight to its ledger. It draws from the same pool of
            accounts as{" "}
            <span className="geist-medium text-[#111827]">Payments Received</span>,{" "}
            <span className="geist-medium text-[#111827]">Payments Made</span>,
            and{" "}
            <span className="geist-medium text-[#111827]">Bank Transfer Logs</span>.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Account types */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Three kinds of accounts, one balance pool
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Every account here sits in the same pool. You can{" "}
            <span className="geist-medium text-[#111827]">deposit</span> into
            it, <span className="geist-medium text-[#111827]">withdraw</span>{" "}
            from it, or{" "}
            <span className="geist-medium text-[#111827]">transfer</span>{" "}
            between it and any other account the same way, whatever its type.
          </p>
          <Definitions
            items={[
              {
                term: "Bank",
                desc: "Your company's actual current account, savings account, or overdraft. You set this one up yourself.",
              },
              {
                term: "Petty Cash",
                desc: "The small day-to-day cash a business keeps on hand outside a bank.",
              },
              {
                term: "Payment Clearing",
                desc: 'Shown as "Undeposited Funds." Money that\'s been received but not yet matched to an actual bank deposit.',
              },
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mt-3">
            Only <span className="geist-medium text-[#111827]">Bank</span> is
            something you create. The system provisions the other two on its
            own, which is why{" "}
            <span className="geist-medium text-[#111827]">Add Bank</span> has
            no account-type choice.
          </p>
          <Screenshot caption="Screenshot: the Banks list showing the Type column with Bank / Petty Cash / Payment Clearing rows side by side." />

          {/* 2. Adding links to ledger */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Adding an account links it to your ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Add a bank account and it becomes a real account in your{" "}
            <span className="geist-medium text-[#111827]">Chart of Accounts</span>{" "}
            that every other module can post against.
          </p>
          <Definitions
            items={[
              {
                term: "Make this primary",
                desc: "Marks this account as the company primary bank. Sales documents (Quote, Sales Order, Delivery Challan, Invoice) refuse to create until one account is primary. The tag Primary shows on the list.",
              },
              {
                term: "Currency",
                desc: "Locks the moment you save. Every transaction the account ever records depends on it. The list of currencies comes from Settings → Currencies.",
              },
              {
                term: "Account Code, Bank Name, IFSC, GSTIN, Division, Branch",
                desc: "Identifying detail. Change any of these whenever you like.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Bank form, with Currency highlighted as the one field that locks after saving." />

          {/* 3. Deposit/Withdraw/Transfer as one mechanism */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Deposit, Withdraw, and Transfer are one mechanism
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three starting points, one underlying move: one account is{" "}
            <span className="geist-medium text-[#111827]">debited</span>, the
            other <span className="geist-medium text-[#111827]">credited</span>,
            both balances update immediately, and the entry lands in{" "}
            <span className="geist-medium text-[#111827]">Bank Transfer Logs</span>.
          </p>
          <Definitions
            items={[
              {
                term: "Deposit",
                desc: "Started from an account's own page. Money comes in.",
              },
              {
                term: "Withdraw",
                desc: "Started from an account's own page. Money goes out.",
              },
              {
                term: "Transfer",
                desc: "Started from the Banks list. Moves money between two accounts directly.",
              },
              {
                term: "Cross-currency",
                desc: "Converted automatically. Both currencies stay on the entry. See Multi-Currency.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: a cross-currency Bank Transfer entry showing both currency legs on the amount." />

          {/* 4. Live mirror */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Every account is a live mirror, not a manual record
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Most of an account&apos;s history doesn&apos;t come from this module
            at all.
          </p>
          <Definitions
            items={[
              {
                term: "Payment Made",
                desc: "Posts the moment a bill is settled from this account.",
              },
              {
                term: "Payment Received",
                desc: "Posts the moment an invoice is settled into this account.",
              },
              {
                term: "Expenses",
                desc: "Post the moment they're settled from petty cash.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: Bank History showing a Payment Made entry alongside a Bank Transfer entry." />

          {/* 5. What's locked */}
          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. What&apos;s locked and what isn&apos;t
          </h3>
          <Callout type="warning">
            <span className="geist-medium">Currency</span> locks after the
            account is saved.{" "}
            <span className="geist-medium">Delete / deactivate</span> is not
            available, ever. Everything else is editable at any time.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[16px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/banking/bank-transfer-logs"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Bank Transfer Logs <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The shared log every Deposit, Withdrawal, and Transfer posts to
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Chart of Accounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Understand how bank accounts map to your ledger
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/currencies"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Currencies <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              How multi-currency accounts and conversions work
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
